import { alert } from '@/services/alert';
import {
  isAllowedUploadDocumentType,
  MAX_UPLOAD_DOCUMENT_SIZE_BYTES,
  uploadDocumentToPatient,
} from '@/services/documents';
import { formatFileSize } from '@/services/attachments';
import {
  ActionIcon,
  Box,
  Button,
  Center,
  FileButton,
  Flex,
  Group,
  Image,
  Modal,
  Stack,
  Switch,
  Text,
  Tooltip,
} from '@mantine/core';
import { Patient, Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconCloudUpload, IconFile, IconTrash, IconUpload } from '@tabler/icons-react';
import { ReactElement, useState } from 'react';

type UploadDocumentModalProps = {
  opened: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
  patient: Patient;
  practitioner: Practitioner;
};

export function UploadDocumentModal({
  opened,
  onClose,
  onUploadSuccess,
  patient,
  practitioner,
}: UploadDocumentModalProps): ReactElement {
  const medplum = useMedplum();
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [visibleToPatient, setVisibleToPatient] = useState(false);

  const handleFileSelect = (selectedFiles: File[] | null) => {
    if (selectedFiles && selectedFiles.length > 0) {
      const invalidType = selectedFiles.filter((file) => !isAllowedUploadDocumentType(file));
      if (invalidType.length > 0) {
        alert.error({
          title: 'Unsupported file type',
          message: 'Only PDF and image files are supported.',
        });
        return;
      }

      const tooLarge = selectedFiles.filter((file) => file.size > MAX_UPLOAD_DOCUMENT_SIZE_BYTES);
      if (tooLarge.length > 0) {
        alert.error({
          title: 'File too large',
          message: 'Each file must be 10MB or smaller.',
        });
        return;
      }

      setFiles((prev) => [...prev, ...selectedFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      alert.error('Please select at least one file to upload.');
      return;
    }

    if (isUploading) return;

    setIsUploading(true);
    try {
      await uploadDocumentToPatient(medplum, patient, practitioner, files, { visibleToPatient });
      alert.success('Documents uploaded successfully');
      setFiles([]);
      setVisibleToPatient(false);
      onUploadSuccess();
      onClose();
    } catch (error) {
      alert.error(`Failed to upload documents: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setFiles([]);
      setVisibleToPatient(false);
      onClose();
    }
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Upload Documents" centered size="md">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Select files to upload to this patient's timeline. Documents will be visible to all authorized providers.
        </Text>

        <Switch
          label="Visible to patient"
          description="When enabled, the patient can view this document in the patient app."
          checked={visibleToPatient}
          onChange={(event) => setVisibleToPatient(event.currentTarget.checked)}
          disabled={isUploading}
        />

        {files.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              Selected files
            </Text>
            <Flex gap="xs" wrap="wrap">
              {files.map((file, index) => (
                <Box
                  key={index}
                  p="xs"
                  style={{
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    width: 'fit-content',
                    maxWidth: '300px',
                    position: 'relative',
                  }}
                >
                  <Group gap="sm" align="center" style={{ width: '100%' }}>
                    {file.type.startsWith('image/') ? (
                      <Image
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        style={{
                          width: '40px',
                          height: '40px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Center w={40} h={40}>
                        <IconFile size={20} color="#6c757d" />
                      </Center>
                    )}
                    <Box pr="xl" style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" truncate fw={500}>
                        {file.name}
                      </Text>
                      <Text size="xxs" c="dimmed">
                        {formatFileSize(file.size)}
                      </Text>
                    </Box>
                    <Tooltip label="Remove file" withArrow>
                      <ActionIcon
                        variant="subtle"
                        color="black"
                        onClick={() => handleRemoveFile(index)}
                        size="sm"
                        style={{ flexShrink: 0 }}
                        disabled={isUploading}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Box>
              ))}
            </Flex>
          </Stack>
        )}

        <Group justify="space-between" mt="md">
          <FileButton onChange={handleFileSelect} multiple disabled={isUploading} accept="application/pdf,image/*">
            {(props) => (
              <Button {...props} variant="subtle" rightSection={<IconCloudUpload size={16} />} color="#375462">
                Add files
              </Button>
            )}
          </FileButton>
          <Group gap="xs">
            <Button variant="subtle" onClick={handleClose} disabled={isUploading} color="red">
              Cancel
            </Button>
            <Button
              rightSection={<IconUpload size={16} />}
              onClick={handleUpload}
              disabled={files.length === 0 || isUploading}
              loading={isUploading}
              color="white"
              bg="green.9"
            >
              Upload
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
